import {Badge, HoverCard} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {IconInfoCircle} from '@tabler/icons-react';
import React, {useCallback, useMemo} from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import type {FeatureFlagsMetadata} from 'src/api/ProactApiClient';
import {BulkToggleLinks} from 'src/design/atoms/BulkToggleLinks';
import {SectionCard} from 'src/design/atoms/SectionCard';
import type {IFormDataV2} from 'src/types/FormV2';
import type {FeatureFlagKey, InstanceFormValues, InstanceSectionProps} from './SectionProps';

interface FeatureFlag {
  name: FeatureFlagKey;
  label: string;
  description?: string;
}

const FEATURE_FLAGS: FeatureFlag[] = [
  {name: 'actions', label: 'Actions'},
  {name: 'analytics', label: 'Analytics'},
  {name: 'approvals', label: 'Approvals'},
  {name: 'assistant', label: 'Assistant'},
  {
    name: 'auditLogs',
    label: 'Audit Logs',
    description: 'Default retention: 7 days',
  },
  {
    name: 'badgePrinting',
    label: 'Badge Printing',
    description: 'Enables on-demand badge generation and printing on the Personnel Badge tab',
  },
  {name: 'bulletins', label: 'Bulletins'},
  {name: 'clients', label: 'Clients'},
  {name: 'compliance', label: 'Compliance'},
  {name: 'documentManagement', label: 'Documents'},
  {
    name: 'documentReviews',
    label: 'Document Reviews',
    description: 'Approval/acknowledgment workflow for Document Management folders',
  },
  {name: 'forms', label: 'Forms'},
  {name: 'inspections', label: 'Inspections'},
  {name: 'itemTracker', label: 'Item Tracker'},
  {name: 'jobHazardAnalysis', label: 'JHA'},
  {name: 'knowledgeBase', label: 'Knowledge Base'},
  {name: 'osha', label: 'OSHA'},
  {name: 'safetyEvents', label: 'Safety Events'},
  {name: 'toolboxTalks', label: 'Toolbox Talks'},
  {name: 'training', label: 'Training'},
  {name: 'vendors', label: 'Vendors'},
  {name: 'workflows', label: 'Workflows'},
];

export {FEATURE_FLAGS};

// Derives the FeatureFlagsMetadata save payload from the canonical flag list,
// so adding a flag here is all that's needed — the persist path stays in sync.
export const buildFeatureFlagsMetadata = (
  formData: IFormDataV2
): FeatureFlagsMetadata =>
  FEATURE_FLAGS.reduce<FeatureFlagsMetadata>(
    (acc, flag) => ({...acc, [flag.name]: !!formData[flag.name]}),
    {}
  );

export const FeatureFlagsSection: React.FC<InstanceSectionProps> = ({
  mode,
}) => {
  const {control, setValue} = useFormContext<InstanceFormValues>();
  const formData = useWatch({control});
  const isViewMode = mode === 'view';

  const enabledCount = useMemo(
    () => FEATURE_FLAGS.filter(f => !!formData[f.name]).length,
    [formData]
  );

  const handleEnableAll = useCallback(() => {
    FEATURE_FLAGS.forEach(f => {
      if (!formData[f.name]) {
        setValue(f.name, true, {shouldDirty: true});
      }
    });
  }, [formData, setValue]);

  const handleDisableAll = useCallback(() => {
    FEATURE_FLAGS.forEach(f => {
      if (formData[f.name]) {
        setValue(f.name, false, {shouldDirty: true});
      }
    });
  }, [formData, setValue]);

  const rightSection = (
    <Group gap="xs">
      <Badge size="sm" variant="light" color="blue">
        {enabledCount} of {FEATURE_FLAGS.length} enabled
      </Badge>
      {!isViewMode && (
        <BulkToggleLinks
          size="xs"
          onEnableAll={handleEnableAll}
          onDisableAll={handleDisableAll}
          testIdPrefix="features"
        />
      )}
    </Group>
  );

  return (
    <SectionCard
      title="Feature Flags"
      rightSection={rightSection}
      data-testid="section-feature-flags">
      <Box p="md">
        {isViewMode ? (
          <Group gap="xs" data-testid="feature-flags-view">
            {FEATURE_FLAGS.map(flag => (
              <Badge
                key={flag.name}
                data-testid={`feature-flag-badge-${flag.name}`}
                size="lg"
                variant="light"
                color={formData[flag.name] ? 'green' : 'gray'}
                style={{
                  textTransform: 'none',
                  opacity: formData[flag.name] ? 1 : 0.6,
                }}>
                {flag.label}
              </Badge>
            ))}
          </Group>
        ) : (
          <SimpleGrid
            cols={{base: 1, sm: 2, md: 3}}
            spacing="sm"
            data-testid="feature-flags-edit">
            {FEATURE_FLAGS.map(flag => (
              <Controller
                key={flag.name}
                name={flag.name}
                control={control}
                render={({field}) => (
                  <Switch
                    data-testid={`form-field-${flag.name}`}
                    label={
                      flag.description ? (
                        <Group gap={4} wrap="nowrap">
                          <span>{flag.label}</span>
                          <HoverCard
                            width={300}
                            shadow="md"
                            openDelay={200}
                            closeDelay={400}
                            withArrow>
                            <HoverCard.Target>
                              <IconInfoCircle
                                size={14}
                                style={{
                                  opacity: 0.5,
                                  flexShrink: 0,
                                  cursor: 'help',
                                }}
                              />
                            </HoverCard.Target>
                            <HoverCard.Dropdown>
                              <Text size="xs" fw={600} mb={4}>Description</Text>
                              <Text size="sm">{flag.description}</Text>
                            </HoverCard.Dropdown>
                          </HoverCard>
                        </Group>
                      ) : (
                        flag.label
                      )
                    }
                    color="green.8"
                    checked={!!field.value}
                    onChange={e => field.onChange(e.currentTarget.checked)}
                  />
                )}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>
    </SectionCard>
  );
};
